import Box from "@/components/Box";
import SnpImpactSymbol from "@/components/diagrams/shared/SnpImpactSymbol";
import CircleButton from "@/components/CircleButton";
import { GenesAllelesText } from "@/tpo/ChartCards";
import { InternalTextLink } from "@/components/Links";
import { SpanText } from "@/components/Text";
import TrendIcon from "@/images/TrendIcon";
import Accordion, { AccordionContext } from "@/tpo/Accordion";
import Group from "@/tpo/Group";
import Stack from "@/tpo/Stack";
import styled from "styled-components";

import RichText from "../RichText";
import { Badge } from "./Badges";

const FunctionalMarkerRowContainer = styled(Group)`
  border-bottom: 1px solid #f2f2f2;
`;

export function formatFunctionalMarkerSubtitle(sampleType, dateExtracted) {
  const parts = [];

  if (sampleType) {
    parts.push(sampleType);
  }

  if (dateExtracted) {
    parts.push(
      `Tested: ${new Date(dateExtracted).toLocaleDateString(undefined, {
        day: "numeric",
        month: "numeric",
        year: "numeric"
      })}`
    );
  }

  return parts.length ? parts.join(" | ") : null;
}

export function FunctionalMarkerRow({
  title,
  sampleType,
  dateExtracted,
  accentColor,
  badgeText,
  state,
  isOrdered,
  isUntested,
  productLinkText,
  productLinkHref,
  units,
  onClick
}) {
  const orderedState = state === "ordered" || isOrdered;
  const untestedState = state === "untested" || isUntested;
  const statusState = orderedState || untestedState;
  const functionalProductLinkText = productLinkText;
  const functionalProductLinkHref = productLinkHref;
  const subtitle = formatFunctionalMarkerSubtitle(sampleType, dateExtracted);

  let badge = null;

  if (untestedState) {
    badge = (
      <Badge
        bg="transparent"
        color="dark"
        fontFamily="gilroyBold"
        fontSize={9}
        lineHeight="10px"
        py="8px"
        px={16}
        order={[6, 6, 3]}
        mr={[16, 16, "auto"]}
        withBorder
      >
        untested
      </Badge>
    );
  } else if (badgeText) {
    badge = (
      <Badge
        bg={accentColor}
        color="white"
        fontSize={14}
        lineHeight="24px"
        py="8px"
        px={16}
        order={[6, 6, 3]}
        mr={[16, 16, "auto"]}
      >
        {badgeText}
      </Badge>
    );
  }

  return (
    <FunctionalMarkerRowContainer
      bg={untestedState ? "#E5E5E5" : "white"}
      minHeight={80}
      alignItems="center"
      justifyContent="space-between"
      px={[16, 20, 28]}
      py={[14, 14, 16]}
      borderBottom="1px solid #F2F2F2"
      gap={12}
      flexWrap="wrap"
      onClick={!statusState && onClick}
      cursor={!statusState && "pointer"}
    >
      <Group alignItems="center" gap={[12, 16, 20]} flexGrow={1} maxWidth={400}>
        <Box width={8} height={48} borderRadius={4} bg={accentColor} />
        <Box display="flex" flexDirection="column" gap={2}>
          <SpanText fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="24px">
            {title}
          </SpanText>
          {subtitle && !untestedState ? (
            <SpanText fontSize={12} color="#818181" lineHeight="16px">
              {subtitle}
            </SpanText>
          ) : null}
        </Box>
      </Group>
      <Box display={["block", "block", "none"]} width="100%" order={[5, 5, 2]} />
      {badge}
      {!statusState && (
        <SpanText order={[7, 7, 4]} mr="auto" fontSize={14}>
          <SpanText fontSize="inherit">{units}</SpanText>
        </SpanText>
      )}
      {untestedState && functionalProductLinkHref && functionalProductLinkText && (
        <InternalTextLink order={[7, 7, 5]} href={functionalProductLinkHref}>
          {functionalProductLinkText}
        </InternalTextLink>
      )}
      {!statusState && (
        <Box
          order={[2, 2, 6]}
          width={48}
          height={48}
          borderRadius="50%"
          bg="haze"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <TrendIcon fill="black" size={15} withCircle={false} />
        </Box>
      )}
    </FunctionalMarkerRowContainer>
  );
}

export function SnpAccordionRow({ row }) {
  const rowValue = `snp-row-${row.badgeText}-${row.title}`;
  const isUntested = row?.state === "untested" || row?.isUntested;
  const isOrdered = row?.state === "ordered" || row?.isOrdered;
  const productLinkText = row?.productLinkText;
  const productLinkHref = row?.productLinkHref || row?.productHref || row?.href;
  const title = row?.title;
  const subtitle = row?.subtitle;
  const badgeText = row?.badgeText;

  const orderedBadge = isOrdered ? (
    <Badge
      bg="transparent"
      color="dark"
      fontSize={12}
      lineHeight="24px"
      py="8px"
      px={20}
      textTransform="uppercase"
      letterSpacing="1.5px"
      order={[6, 6, 2]}
      mt={[-16, -16, 0]}
      withBorder
    >
      dispatched
    </Badge>
  ) : null;

  const untestedBadge = isUntested ? (
    <Badge
      bg="dark"
      color="white"
      fontSize={10}
      lineHeight="24px"
      py="8px"
      px={20}
      textTransform="uppercase"
      letterSpacing="1.5px"
      order={[6, 6, 2]}
      mr="auto"
    >
      no read
    </Badge>
  ) : null;

  const badge =
    !untestedBadge && !orderedBadge ? (
      <Group alignItems="center" gap={10} order={[6, 6, 2]} mr="auto">
        <GenesAllelesText
          color={row.accentColor}
          fontFamily="gilroyMedium"
          fontSize={14}
          text={badgeText}
          size={28}
        />
        {row.symbol ? <SnpImpactSymbol symbol={row.symbol} fill="black" size={14} /> : null}
      </Group>
    ) : null;

  const productLink =
    !isOrdered && productLinkHref && productLinkText ? (
      <InternalTextLink href={productLinkHref} order={[7, 7, 3]}>
        {productLinkText}
      </InternalTextLink>
    ) : null;

  return (
    <Accordion.Item value={rowValue}>
      <AccordionContext.Consumer>
        {({ openState, open, close }) => {
          const isOpen = openState.has(rowValue);

          const chevron =
            !untestedBadge && !orderedBadge ? (
              <CircleButton
                direction={isOpen ? "top" : "bottom"}
                iconWidth={10}
                iconHeight={10}
                onClick={() => (isOpen ? close(rowValue) : open(rowValue))}
                sx={{
                  order: [7, 7, 3],
                  bg: "#E5E5E5"
                }}
              />
            ) : null;

          let bg = "white";
          if (isOpen) {
            bg = "haze";
          } else if (isUntested || isOrdered) {
            bg = "#E5E5E5";
          }

          return (
            <>
              <Group
                bg={bg}
                minHeight={80}
                alignItems="center"
                justifyContent="space-between"
                px={[20, 20, 28]}
                py={16}
                gap={12}
                flexWrap="wrap"
              >
                <Group alignItems="center" gap={[12, 16, 20]} flexGrow={1} order={1} maxWidth={400}>
                  <Box width={8} height={48} borderRadius={4} bg={row.accentColor} />
                  <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="28px">
                    {title}
                  </Box>
                  {!!subtitle && (
                    <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="28px">
                      {subtitle}
                    </Box>
                  )}
                </Group>
                <Box display={["block", "block", "none"]} width="100%" order={[5, 5, 2]} />
                {orderedBadge}
                {untestedBadge}
                {badge}
                {productLink}
                {chevron}
              </Group>
              <Accordion.Panel p={0} bg="haze" borderRadius={0}>
                <Stack px={[16, 20, 28 + 40]} pb={[40, 40, 60]} pt={2} gap={[20, 20, 28]}>
                  <Stack gap={16}>
                    <Box
                      fontFamily="gilroyBold"
                      fontSize={[16, 16, 18]}
                      lineHeight={["24px", "24px", "28px"]}
                    >
                      Interpretation
                    </Box>
                    <RichText
                      fontSize={14}
                      lineHeight="24px"
                      style={{
                        whiteSpace: "normal"
                      }}
                    >
                      {row.interpretation}
                    </RichText>
                  </Stack>
                  <Stack gap={16}>
                    <Box
                      fontFamily="gilroyBold"
                      fontSize={[16, 16, 18]}
                      lineHeight={["24px", "24px", "28px"]}
                    >
                      Suggestions
                    </Box>
                    <RichText
                      fontSize={14}
                      lineHeight="24px"
                      style={{
                        whiteSpace: "normal"
                      }}
                    >
                      {row.suggestion}
                    </RichText>
                  </Stack>
                </Stack>
              </Accordion.Panel>
            </>
          );
        }}
      </AccordionContext.Consumer>
    </Accordion.Item>
  );
}

export function UntestedMarkersRow({ title, linkText }) {
  return (
    <Group
      bg="haze"
      minHeight={80}
      alignItems="center"
      justifyContent="space-between"
      px={[16, 20, 28]}
      py={[14, 14, 16]}
      borderBottom="1px solid #E5E5E5"
      gap={12}
    >
      <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="24px">
        {title}
      </Box>
      <InternalTextLink>{linkText}</InternalTextLink>
    </Group>
  );
}

export function PotentialSymptomsRow({ title, risk, color }) {
  return (
    <Group
      bg="white"
      minHeight={64}
      alignItems="center"
      justifyContent="space-between"
      px={[16, 20, 28]}
      py={[12, 12, 14]}
      borderBottom="1px solid #E5E5E5"
      gap={12}
    >
      <Box fontFamily="gilroyMedium" fontSize={[16, 16, 18]} lineHeight="28px">
        {title}
      </Box>
      <Box fontFamily="gilroyBold" fontSize={[14, 14, 16]} color={color} lineHeight="28px">
        {risk}
      </Box>
    </Group>
  );
}

export function InterpretationSection({ label, testedRows, untestedRows }) {
  const listUi = (
    <Stack gap={10}>
      <Badge mr="auto">{label}</Badge>
      <Stack gap={0}>
        {testedRows.map((row, idx) => (
          <FunctionalMarkerRow
            title={row.title}
            sampleType={row.sampleType}
            dateExtracted={row.dateExtracted}
            accentColor={row.accentColor}
            badgeText={row.badgeText}
            units={row.units}
            key={`${label}-${idx}`}
            onClick={row.onClick}
          />
        ))}
        {untestedRows.map((row, idx) => (
          <FunctionalMarkerRow
            title={row.title}
            key={`${label}-${idx}`}
            state="untested"
            productLinkText={row.productName}
            productLinkHref={row.productLink}
            accentColor="midGrey"
            badgeText="untested"
          />
        ))}
      </Stack>
    </Stack>
  );

  return testedRows?.length || untestedRows?.length ? listUi : null;
}

export function SnpSection({ testedRows, untestedRows }) {
  const listUi = (
    <Stack gap={16}>
      <Badge mr="auto">SNPS</Badge>
      <Stack overflow="hidden" gap={0}>
        <Accordion size={0}>
          {testedRows.map((row, idx) => (
            <SnpAccordionRow
              key={`snp-${row.badgeText}-${idx}`}
              row={row}
              isLast={idx === testedRows.length - 1}
            />
          ))}
          {untestedRows.map((row, idx) => (
            <SnpAccordionRow
              key={`untested-snp-${idx}`}
              row={row}
              isLast={idx === untestedRows.length - 1}
              state="untested"
            />
          ))}
        </Accordion>
      </Stack>
    </Stack>
  );

  return testedRows?.length || untestedRows?.length ? listUi : null;
}
